import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { router, usePage, Link } from '@inertiajs/react'

/**
 * A reusable form component that integrates React Hook Form with Inertia.js
 *
 * Features:
 * - Automatic form state management via React Hook Form
 * - Optional client-side validation with Zod schemas
 * - Server-side validation error mapping from Rails
 * - Automatic FormData handling for file uploads
 * - Consistent submit/cancel button styling
 *
 * @example
 * import { facilitySchema } from '../../../schemas'
 *
 * <ResourceForm
 *   schema={facilitySchema}
 *   resource="facility"
 *   endpoint="/manage/facilities"
 *   backLink="/manage/facilities"
 *   submitLabel="Create Facility"
 * >
 *   {({ control, errors }) => (
 *     <>
 *       <FormFieldController control={control} name="name" label="Name" required errors={errors} />
 *     </>
 *   )}
 * </ResourceForm>
 */
export default function ResourceForm({
  schema,
  resource,
  endpoint,
  method = 'post',
  defaultValues = {},
  extraData = {},
  backLink,
  submitLabel = 'Save',
  children
}) {
  const { errors: serverErrors } = usePage().props

  // Configure form with optional Zod resolver
  const formConfig = {
    defaultValues,
    ...(schema && { resolver: zodResolver(schema) })
  }

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm(formConfig)

  // Map Rails server errors to React Hook Form
  useEffect(() => {
    if (serverErrors) {
      Object.entries(serverErrors).forEach(([field, message]) => {
        setError(field, {
          type: 'server',
          message: Array.isArray(message) ? message[0] : message
        })
      })
    }
  }, [serverErrors, setError])

  const onSubmit = (data) => {
    const merged = { ...data, ...extraData }
    // Optional fields preprocessed to undefined by Zod must be sent as empty
    // strings so Rails treats them as nil instead of leaving the existing value.
    Object.keys(merged).forEach((key) => {
      if (merged[key] === undefined) merged[key] = ''
    })

    const hasFile = Object.values(merged).some(v => v instanceof File)

    if (hasFile) {
      const formData = buildFormData(resource, merged)
      if (method === 'patch') {
        formData.append('_method', 'PATCH')
      }
      router.post(endpoint, formData, { forceFormData: true })
    } else {
      const payload = { [resource]: merged }
      if (method === 'patch') {
        router.patch(endpoint, payload)
      } else {
        router.post(endpoint, payload)
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
      {children({ control, errors, watch, setValue })}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <Link
          href={backLink}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </Link>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white bg-sky-600 border border-transparent rounded-md hover:bg-sky-700 disabled:opacity-50"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  )
}

/**
 * Build FormData from an object, properly namespacing for Rails
 */
function buildFormData(resource, data) {
  const formData = new FormData()

  Object.entries(data).forEach(([key, value]) => {
    // Skip null/undefined but allow empty strings and false
    if (value === null || value === undefined) return
    // Skip file fields that weren't changed
    if (key.includes('image') && !(value instanceof File)) return
    if (['logo', 'asset'].includes(key) && !(value instanceof File)) return

    // Handle arrays (e.g., position_ids: ["id1", "id2"])
    if (Array.isArray(value)) {
      value.forEach((item) => {
        formData.append(`${resource}[${key}][]`, item)
      })
      return
    }

    formData.append(`${resource}[${key}]`, value)
  })

  return formData
}
